/**
 * Ghee or butter for one day, on the customer's round, from Change delivery (the owner, 2026-11-04). It goes through api.place_product_order, the
 * one-time order the CRM already uses, not the milk basket: a jar never joins an agreement, its bill with GST comes when the rider delivers it, and the
 * server keeps the day's cut-off rules.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { rpc } from '../../lib/supabase';
import { Button, plainError } from '../../ui/primitives';
import { toast } from '../../ui/toast';

export const isGheeOrButter = (code: string): boolean => /^GHEE|^BUTTER/.test(code);

/** The active jars and packs a customer can be given. */
export const gheeItems = <V extends { code: string; status: string }>(variants: readonly V[]): V[] =>
  variants.filter((v) => isGheeOrButter(v.code) && v.status === 'ACTIVE');

export const gheeOrderArgs = (customerId: string, day: string, code: string, quantity: number, key: string) => ({
  p_customer_id: customerId, p_date: day, p_mode: 'ROUND', p_lines: [{ variantCode: code, quantity }], p_note: null, p_idempotency_key: key,
});

export function GheeAddOn({ customerId, day, dayWords, onRound, variants }: {
  customerId: string; day: string; dayWords: string; onRound: boolean; variants: readonly { code: string; name: string; status: string }[];
}) {
  const qc = useQueryClient();
  const items = gheeItems(variants);
  const [code, setCode] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [key, setKey] = useState(() => crypto.randomUUID());
  const place = useMutation({
    mutationFn: () => rpc<{ detail?: string }>('place_product_order', gheeOrderArgs(customerId, day, code, quantity, key)),
    onSuccess: (r) => {
      toast(r.detail ?? `Added to ${dayWords}.`, { tone: 'ok', ms: 7000 });
      setCode(''); setQuantity(1); setKey(crypto.randomUUID());
      for (const k of ['customer-calendar', 'customer-changes', 'exceptions-days', 'one-time-orders', 'customer-360']) void qc.invalidateQueries({ queryKey: [k] });
    },
  });
  if (items.length === 0) return null;
  const control = 'h-[30px] rounded-[var(--radius-control)] border border-line-strong bg-surface px-2 text-small';
  return (
    <div className="grid gap-2 rounded-[var(--radius-control)] border border-line p-2">
      <div>
        <div className="font-medium text-ink">Ghee or butter · {dayWords} only</div>
        <div className="text-ink-muted">On the round, for this one day. Its bill with GST comes when the rider delivers it. Saved on its own button; Save below does not include it.</div>
      </div>
      {onRound ? (
        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Ghee or butter" value={code} onChange={(e) => setCode(e.target.value)} className={control}>
            <option value="">Choose an item…</option>
            {items.map((v) => <option key={v.code} value={v.code}>{v.code} · {v.name}</option>)}
          </select>
          <input type="number" aria-label="How many" min={1} max={50} value={quantity} onChange={(e) => setQuantity(Math.min(50, Math.max(1, Number(e.target.value) || 1)))} className={`${control} w-16`} />
          <Button size="dense" disabled={!code || place.isPending} onClick={() => place.mutate()}>Add to the round</Button>
        </div>
      ) : <p className="text-refused">This customer is not on a round, so it cannot go on one.</p>}
      {place.error ? <p role="alert" className="text-refused">{plainError((place.error as Error).message)}</p> : null}
    </div>
  );
}
